// pages/About.js
import React, { useEffect, useRef } from "react";
import Header from "../components/SetNavbar";
import Footer from "../components/Footer";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  Divider,
  Fade,
  Slide,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Button,
  Stack,
} from "@mui/material";
import {
  School,
  Code,
  Security,
  Storage,
  Psychology,
  TrendingUp,
  People,
  EmojiObjects,
  CheckCircle,
  ArrowForward,
  VideoCall,
  Schedule,
  WorkspacePremium,
  Groups,
  Analytics,
  MenuBook,
  RocketLaunch,
  Verified,
  Star,
} from "@mui/icons-material";
import Pic from "../assets/AboutUs/girl2.png";
import "../styles/About.css";

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const sectionRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: <EmojiObjects sx={{ fontSize: 40 }} />,
      title: "Innovation",
      description: "Pushing boundaries with cutting-edge technology",
      color: "#2BAD65",
      bgColor: "rgba(43, 173, 101, 0.12)",
    },
    {
      icon: <People sx={{ fontSize: 40 }} />,
      title: "Community",
      description: "Building a supportive learning community",
      color: "#A0EF48",
      bgColor: "rgba(160, 239, 72, 0.12)",
    },
    {
      icon: <WorkspacePremium sx={{ fontSize: 40 }} />,
      title: "Excellence",
      description: "Delivering top-quality education",
      color: "#FFD93D",
      bgColor: "rgba(255, 217, 61, 0.12)",
    },
    {
      icon: <Analytics sx={{ fontSize: 40 }} />,
      title: "Growth",
      description: "Continuous learning and improvement",
      color: "#FF6B6B",
      bgColor: "rgba(255, 107, 107, 0.12)",
    },
  ];

  const courses = [
    {
      icon: <Psychology />,
      title: "Artificial Intelligence",
      description:
        "Master AI, machine learning, deep learning, and natural language processing",
      color: "#2BAD65",
    },
    {
      icon: <Storage />,
      title: "Microsoft Dynamics 365",
      description:
        "In-depth knowledge of sales, customer service, marketing, and finance modules",
      color: "#A0EF48",
    },
    {
      icon: <Code />,
      title: "JavaScript Development",
      description:
        "Core concepts, React, Angular, and interactive web applications",
      color: "#FFD93D",
    },
    {
      icon: <Storage />,
      title: "Mobile & Web App Development",
      description: "Android, iOS, React Native, and responsive applications",
      color: "#DDA0DD",
    },
    {
      icon: <Security />,
      title: "Cybersecurity",
      description: "Network security, ethical hacking, and security testing",
      color: "#FF6B6B",
    },
    {
      icon: <TrendingUp />,
      title: "Agile Methodology",
      description: "Scrum, Kanban, and collaborative project management",
      color: "#FF8E53",
    },
  ];

  const additionalCourses = [
    "Cloud Computing",
    "Data Science",
    "DevOps",
    "Blockchain",
    "IoT",
    "Digital Marketing",
  ];

  const stats = [
    {
      number: "10K+",
      label: "Students Trained",
      icon: <School />,
      color: "#2BAD65",
    },
    {
      number: "50+",
      label: "Expert Instructors",
      icon: <Groups />,
      color: "#A0EF48",
    },
    {
      number: "200+",
      label: "Courses Offered",
      icon: <MenuBook />,
      color: "#FFD93D",
    },
    {
      number: "95%",
      label: "Success Rate",
      icon: <TrendingUp />,
      color: "#FF6B6B",
    },
  ];

  const features = [
    {
      icon: <VideoCall />,
      title: "Live Sessions",
      description: "Real-time interactive classes with expert instructors",
      color: "#2BAD65",
    },
    {
      icon: <Schedule />,
      title: "Flexible Schedule",
      description: "Learn at your own pace with flexible timing options",
      color: "#A0EF48",
    },
    {
      icon: <School />,
      title: "Hands-on Projects",
      description: "Apply knowledge through real-world projects",
      color: "#FFD93D",
    },
    {
      icon: <Groups />,
      title: "Community Support",
      description: "Connect with peers and industry professionals",
      color: "#FF6B6B",
    },
  ];

  return (
    <>
      <Header />
      <Box
        sx={{
          background: "linear-gradient(180deg, #F7F8F2 0%, #e8efe6 100%)",
          minHeight: "100vh",
        }}
      >
        {/* Hero Section */}
        <Box
          className="about-hero"
          sx={{
            pt: { xs: 10, md: 14 },
            pb: { xs: 6, md: 8 },
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Animated Background Decorations */}
          <Box
            sx={{
              position: "absolute",
              top: -150,
              right: -150,
              width: 500,
              height: 500,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(43, 173, 101, 0.05) 0%, transparent 70%)",
              zIndex: 0,
              animation: "pulse 8s ease-in-out infinite",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -150,
              left: -150,
              width: 500,
              height: 500,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(160, 239, 72, 0.04) 0%, transparent 70%)",
              zIndex: 0,
              animation: "pulse 8s ease-in-out infinite 2s",
            }}
          />

          {/* Floating Particles */}
          {[...Array(6)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                top: `${10 + i * 15}%`,
                left: `${5 + (i % 5) * 20}%`,
                width: `${4 + i * 2}px`,
                height: `${4 + i * 2}px`,
                borderRadius: "50%",
                backgroundColor:
                  i % 2 === 0
                    ? "rgba(43, 173, 101, 0.12)"
                    : "rgba(160, 239, 72, 0.12)",
                animation: `float ${6 + i * 2}s ease-in-out infinite ${i * 1.5}s`,
                zIndex: 0,
              }}
            />
          ))}

          <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Fade in timeout={800}>
                  <Box>
                    <Chip
                      label="📖 About Us"
                      sx={{
                        backgroundColor: "rgba(43, 173, 101, 0.1)",
                        color: "#2BAD65",
                        fontWeight: 600,
                        mb: 3,
                        px: 2,
                        border: "1px solid rgba(43, 173, 101, 0.15)",
                        "&:hover": {
                          backgroundColor: "rgba(43, 173, 101, 0.15)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    />

                    <Typography
                      variant="h1"
                      component="h1"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4.5rem" },
                        color: "#1a1a1a",
                        mb: 2,
                        lineHeight: 1.1,
                      }}
                    >
                      Taking E-Learning
                      <Box
                        component="span"
                        sx={{
                          display: "block",
                          background:
                            "linear-gradient(135deg, #2BAD65, #A0EF48, #2BAD65)",
                          backgroundSize: "200% 200%",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          animation: "gradientMove 4s ease-in-out infinite",
                        }}
                      >
                        To A New Level
                      </Box>
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        color: "rgba(26, 26, 26, 0.7)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.8,
                        maxWidth: "90%",
                        mb: 3,
                      }}
                    >
                      Empowering the next generation of tech professionals with
                      industry-leading training and hands-on experience.
                    </Typography>

                    {/* Trust Badges */}
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{ flexWrap: "wrap", gap: 1 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Verified sx={{ color: "#2BAD65", fontSize: 18 }} />
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(26, 26, 26, 0.4)" }}
                        >
                          Trusted Platform
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Star sx={{ color: "#FFD93D", fontSize: 18 }} />
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(26, 26, 26, 0.4)" }}
                        >
                          4.8/5 Rating
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <School sx={{ color: "#A0EF48", fontSize: 18 }} />
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(26, 26, 26, 0.4)" }}
                        >
                          Expert Instructors
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Fade>
              </Grid>

              <Grid item xs={12} md={6}>
                <Slide direction="left" in timeout={1000}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        maxWidth: 500,
                      }}
                    >
                      {/* Animated Decorative Elements */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: -30,
                          right: -30,
                          width: 120,
                          height: 120,
                          borderRadius: "50%",
                          border: "2px solid rgba(43, 173, 101, 0.1)",
                          zIndex: 0,
                          animation: "spin 20s linear infinite",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: -30,
                          left: -30,
                          width: 100,
                          height: 100,
                          borderRadius: "50%",
                          border: "2px solid rgba(160, 239, 72, 0.08)",
                          zIndex: 0,
                          animation: "spin 25s linear infinite reverse",
                        }}
                      />

                      <Box
                        component="img"
                        src={Pic}
                        alt="About Wavepalm Techub"
                        sx={{
                          width: "100%",
                          height: "auto",
                          borderRadius: 4,
                          border: "1px solid rgba(43, 173, 101, 0.06)",
                          boxShadow:
                            "0 20px 60px rgba(0, 0, 0, 0.06), 0 0 80px rgba(43, 173, 101, 0.02)",
                          position: "relative",
                          zIndex: 1,
                          transition:
                            "transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            transform: "scale(1.02)",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Slide>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Stats Section */}
        <Box
          sx={{
            py: { xs: 4, md: 5 },
            backgroundColor: "rgba(43, 173, 101, 0.03)",
            borderTop: "1px solid rgba(43, 173, 101, 0.06)",
            borderBottom: "1px solid rgba(43, 173, 101, 0.06)",
          }}
        >
          <Container maxWidth="xl">
            <Grid container spacing={2} justifyContent="center">
              {stats.map((stat, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <Fade in timeout={600 + index * 100}>
                    <Box sx={{ textAlign: "center" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor: `${stat.color}10`,
                            borderRadius: "50%",
                            p: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: stat.color,
                          }}
                        >
                          {stat.icon}
                        </Box>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            color: "#1a1a1a",
                            fontSize: { xs: "1.5rem", md: "2rem" },
                            background: `linear-gradient(135deg, ${stat.color}, ${stat.color}80)`,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          }}
                        >
                          {stat.number}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(26, 26, 26, 0.3)",
                          fontSize: "0.75rem",
                          letterSpacing: 0.5,
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* About Content */}
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Fade in timeout={800}>
            <Box>
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                  color: "#1a1a1a",
                  textAlign: "center",
                  mb: 2,
                }}
              >
                About{" "}
                <Box
                  component="span"
                  sx={{
                    background:
                      "linear-gradient(135deg, #2BAD65, #A0EF48, #2BAD65)",
                    backgroundSize: "200% 200%",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    animation: "gradientMove 4s ease-in-out infinite",
                  }}
                >
                  Wavepalm Techub
                </Box>
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "rgba(26, 26, 26, 0.6)",
                  fontSize: { xs: "1rem", md: "1.05rem" },
                  lineHeight: 1.8,
                  textAlign: "center",
                  maxWidth: 750,
                  mx: "auto",
                  mb: 6,
                }}
              >
                We are a leading software development training company that
                offers comprehensive and industry-focused training programs in a
                wide range of technologies and methodologies.
              </Typography>

              {/* Core Values */}
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: "#1a1a1a",
                    textAlign: "center",
                    mb: 4,
                  }}
                >
                  Our Core Values
                </Typography>
                <Grid container spacing={2.5}>
                  {values.map((value, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Fade in timeout={600 + index * 100}>
                        <Paper
                          sx={{
                            p: 3,
                            textAlign: "center",
                            backgroundColor: "rgba(255, 255, 255, 0.6)",
                            border: "1px solid rgba(43, 173, 101, 0.06)",
                            borderRadius: 3,
                            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                              transform: "translateY(-8px)",
                              boxShadow: `0 8px 30px ${value.color}15`,
                              borderColor: value.color,
                              backgroundColor: value.bgColor,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor: value.bgColor,
                              borderRadius: "50%",
                              width: 60,
                              height: 60,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              margin: "0 auto 16px",
                              color: value.color,
                              transition: "all 0.3s ease",
                              "&:hover": {
                                transform: "scale(1.1) rotate(10deg)",
                              },
                            }}
                          >
                            {value.icon}
                          </Box>
                          <Typography
                            variant="h6"
                            sx={{ color: "#1a1a1a", fontWeight: 600, mb: 1 }}
                          >
                            {value.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "rgba(26, 26, 26, 0.5)" }}
                          >
                            {value.description}
                          </Typography>
                        </Paper>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Course Programs */}
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: "#1a1a1a",
                    textAlign: "center",
                    mb: 4,
                  }}
                >
                  Our Training Programs
                </Typography>
                <Grid container spacing={2.5}>
                  {courses.map((course, index) => (
                    <Grid item xs={12} md={6} lg={4} key={index}>
                      <Fade in timeout={600 + index * 100}>
                        <Card
                          sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.6)",
                            border: "1px solid rgba(43, 173, 101, 0.06)",
                            borderRadius: 3,
                            height: "100%",
                            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                              transform: "translateY(-5px)",
                              boxShadow: `0 8px 30px ${course.color}15`,
                              borderColor: course.color,
                              backgroundColor: "rgba(255, 255, 255, 0.8)",
                            },
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mb: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  backgroundColor: `${course.color}10`,
                                  borderRadius: "50%",
                                  p: 1.5,
                                  color: course.color,
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    transform: "scale(1.1) rotate(10deg)",
                                  },
                                }}
                              >
                                {course.icon}
                              </Box>
                              <Typography
                                variant="h6"
                                sx={{
                                  color: "#1a1a1a",
                                  fontWeight: 600,
                                  fontSize: { xs: "1rem", md: "1.1rem" },
                                }}
                              >
                                {course.title}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "rgba(26, 26, 26, 0.5)",
                                lineHeight: 1.6,
                              }}
                            >
                              {course.description}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Additional Courses */}
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  background: "rgba(255, 255, 255, 0.4)",
                  borderRadius: 3,
                  border: "1px solid rgba(43, 173, 101, 0.06)",
                  mb: 6,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: "#1a1a1a",
                    mb: 2,
                    textAlign: "center",
                  }}
                >
                  Additional Programs
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ flexWrap: "wrap", gap: 1, justifyContent: "center" }}
                >
                  {additionalCourses.map((course, index) => (
                    <Chip
                      key={index}
                      label={course}
                      sx={{
                        backgroundColor: "rgba(43, 173, 101, 0.06)",
                        color: "rgba(26, 26, 26, 0.6)",
                        border: "1px solid rgba(43, 173, 101, 0.08)",
                        fontSize: "0.8rem",
                        px: 1,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: "rgba(43, 173, 101, 0.1)",
                          color: "#2BAD65",
                          borderColor: "rgba(43, 173, 101, 0.3)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Box>

              {/* Why Choose Us */}
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: "#1a1a1a",
                    textAlign: "center",
                    mb: 4,
                  }}
                >
                  Why Choose Us?
                </Typography>
                <Grid container spacing={2.5}>
                  {features.map((feature, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Fade in timeout={600 + index * 100}>
                        <Box
                          sx={{
                            p: 3,
                            textAlign: "center",
                            backgroundColor: "rgba(255, 255, 255, 0.4)",
                            borderRadius: 3,
                            border: "1px solid rgba(43, 173, 101, 0.06)",
                            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                              backgroundColor: "rgba(255, 255, 255, 0.6)",
                              transform: "translateY(-5px)",
                              borderColor: feature.color,
                              boxShadow: `0 8px 30px ${feature.color}10`,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor: `${feature.color}10`,
                              borderRadius: "50%",
                              width: 55,
                              height: 55,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              margin: "0 auto 12px",
                              color: feature.color,
                              transition: "all 0.3s ease",
                              "&:hover": {
                                transform: "scale(1.1)",
                              },
                            }}
                          >
                            {feature.icon}
                          </Box>
                          <Typography
                            variant="h6"
                            sx={{
                              color: "#1a1a1a",
                              fontWeight: 600,
                              mb: 1,
                              fontSize: { xs: "1rem", md: "1.1rem" },
                            }}
                          >
                            {feature.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(26, 26, 26, 0.5)",
                              fontSize: "0.85rem",
                            }}
                          >
                            {feature.description}
                          </Typography>
                        </Box>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* CTA Section */}
              <Box
                sx={{
                  textAlign: "center",
                  p: { xs: 4, md: 6 },
                  background:
                    "linear-gradient(135deg, rgba(43, 173, 101, 0.06), rgba(160, 239, 72, 0.04))",
                  borderRadius: 4,
                  border: "1px solid rgba(43, 173, 101, 0.06)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Animated Border */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background:
                      "linear-gradient(90deg, #2BAD65, #A0EF48, #2BAD65)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 3s linear infinite",
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: "#1a1a1a",
                    mb: 2,
                    fontSize: { xs: "1.3rem", md: "1.8rem" },
                  }}
                >
                  Ready to Start Your Journey?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "rgba(26, 26, 26, 0.5)",
                    mb: 3,
                    maxWidth: 500,
                    mx: "auto",
                  }}
                >
                  Join us and embark on a rewarding journey of learning and
                  growth in the field of software development.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  href="/courses"
                  startIcon={<RocketLaunch />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    background: "linear-gradient(135deg, #2BAD65, #A0EF48)",
                    borderRadius: 3,
                    color: "#ffffff",
                    boxShadow: "0 4px 25px rgba(43, 173, 101, 0.3)",
                    "&:hover": {
                      transform: "translateY(-3px) scale(1.02)",
                      boxShadow: "0 8px 35px rgba(43, 173, 101, 0.4)",
                      background: "linear-gradient(135deg, #239A56, #8EDB40)",
                    },
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  Explore Courses
                  <ArrowForward sx={{ ml: 1 }} />
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>
      <Footer />

      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.3; }
            50% { transform: scale(1.1); opacity: 0.6; }
          }

          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes gradientMove {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }

          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}
      </style>
    </>
  );
};

export default About;
